import './Connections.css';

// Libraries
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

// Utils
import { useFetch } from 'utils/rest/request';
import User from 'utils/user.util';

// Components
import Loader from 'components/Loader/Loader';
import Modal from 'components/Modal/Modal';
import Input from 'components/Form/Input/Input';
import AppLogo from 'components/AppLogo/AppLogo';
import { handleOAuth2 } from './OAuth/OAuth2';

// Assets
import { ReactComponent as AddIcon } from 'assets/icons/add_icon.svg';
import { ReactComponent as RemoveIcon } from 'assets/icons/remove_icon.svg';
import { ReactComponent as ErrorIcon } from 'assets/icons/error_icon.svg';
import { ReactComponent as WarningIcon } from 'assets/icons/exclamation-triangle.svg';
import { ReactComponent as RefreshIcon } from 'assets/icons/refresh_icon_24.svg';

const Connections = () => {
    const [searchParams] = useSearchParams();
    const [errorMessage, setErrorMessage] = useState('');
    const [modalIsOpen, setIsOpen] = useState(false);
    const [modalType, setModalType] = useState();
    const [removeConnectionApp, setRemoveConnectionApp] = useState();
    const [newConnectionApp, setNewConnectionApp] = useState();
    const [newConnectionName, setNewConnectionName] = useState();
    const [validationErrors, setValidationErrors] = useState();

    const {
        loading,
        data: apps,
        error,
    } = useFetch('/api/connected_apps', {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const deleteExistingConnection = (connection) => {
        setRemoveConnectionApp(connection);
        setModalType('remove-connection');
        setIsOpen(true);
    };

    const getModalOptions = (type) => {
        switch (type) {
            case 'already-connected':
                return {
                    title: 'Hold on a sec...',
                    content: (
                        <div>
                            <p>This app is already connected.</p>
                        </div>
                    ),
                    width: '250px',
                    textAlign: 'center',
                };
            case 'input-error':
                return {
                    title: <ErrorIcon fill="var(--error-color)" />,
                    content: (
                        <div>
                            <p>Please fill in all fields to proceed.</p>
                        </div>
                    ),
                    width: '250px',
                    textAlign: 'center',
                };
            case 'add-connection':
                const client_dependencies = newConnectionApp?.connection_dependencies.filter(
                    (e) => e.type === 'CLIENT'
                );
                return {
                    title: `New ${newConnectionApp.name} connection`,
                    type: 'confirmation',
                    content: (
                        <div>
                            <p>Connection Name</p>
                            <Input
                                placeholder="Name"
                                setValue={(e) => {
                                    setNewConnectionName(e.target.value);
                                }}
                                marginBottom="10px"
                                error={validationErrors?.newConnectionName}
                                displayError={true}
                            />
                            {client_dependencies.map((e, index) => {
                                return (
                                    <div key={index}>
                                        <p>{e.value}</p>
                                        <Input
                                            placeholder={e.field}
                                            setValue={(e) => {
                                                // @Todo
                                            }}
                                            marginBottom="10px"
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    ),
                    width: '350px',
                    textAlign: 'center',
                    confirmBtnText: 'Connect',
                    confirmationBtnAction: () => {
                        const existingNames = apps.rows.flatMap((app) =>
                            app.connections.map((connection) => connection.name)
                        );

                        // Form validation
                        if (!newConnectionName) {
                            setValidationErrors({
                                newConnectionName: 'Required connection name.',
                            });
                            return { cancel: true };
                        } else if (existingNames.includes(newConnectionName)) {
                            setValidationErrors({
                                newConnectionName: 'Name already exists and must be unique.',
                            });
                            return { cancel: true };
                        }

                        // Allow fow multiple types of authentication
                        switch (newConnectionApp.version) {
                            case 'oauth2.0':
                                return handleOAuth2(newConnectionApp, newConnectionName).then(
                                    (response) => {
                                        if (response?.error) {
                                            setModalType('unavailable');
                                            setIsOpen(true);
                                        }
                                    }
                                );
                            default:
                                return;
                        }
                    },
                };
            case 'remove-connection':
                return {
                    title: <WarningIcon fill="var(--error-color)" width="40" height="40" />,
                    type: 'confirmation',
                    content: (
                        <p>
                            Are you sure you want to remove '{removeConnectionApp.name}' connection?
                        </p>
                    ),
                    width: '300px',
                    textAlign: 'center',
                    confirmBtnText: 'Remove',
                    confirmationBtnAction: () => {
                        fetch(
                            `${process.env.REACT_APP_BACKEND_URL}/api/connected_apps/tenant/connections`,
                            {
                                method: 'DELETE',
                                headers: {
                                    'Content-Type': 'application/json',
                                    Authorization: `Bearer ${User.getToken()}`,
                                },
                                body: JSON.stringify({
                                    connection_id: removeConnectionApp.id,
                                }),
                            }
                        )
                            .then((response) => response.json())
                            .then((data) => {
                                if (data?.error) {
                                    showErrorModal(
                                        'There was an error deleting this App Connection. Make sure there are no Flow routines using this connection.'
                                    );
                                    return;
                                }
                                if (data?.deleted) {
                                    window.location.reload();
                                }
                            })
                            .catch((err) => console.log(err));
                    },
                };
            case 'unavailable':
                return {
                    title: <ErrorIcon fill="var(--error-color)" />,
                    content: (
                        <div>
                            <p>We're currently unable to connect to this app.</p>
                            <p>Try again later.</p>
                        </div>
                    ),
                    width: '350px',
                    textAlign: 'center',
                };
            case 'error':
                return {
                    title: <ErrorIcon fill="var(--error-color)" />,
                    content: <p>{errorMessage}</p>,
                    width: '350px',
                    textAlign: 'center',
                };
            default:
                return {};
        }
    };

    const showErrorModal = (error) => {
        setErrorMessage(error);
        setModalType('error');
        setIsOpen(true);
    };

    useEffect(() => {
        if (!searchParams) return;
        const error = searchParams.get('error');
        if (error) {
            showErrorModal(error);
        }
    }, [searchParams]);

    return (
        <>
            <div className="connections">
                <div className="header">New App connection</div>
                <div className="app-select">
                    {loading && <Loader />}
                    {error && 'There was an error loading apps.'}
                    {apps?.rows?.map((app, i) => {
                        return (
                            <div
                                className={`app-select-item ${
                                    app.status === 'active' ? 'clickable' : ''
                                }`}
                                key={i}
                                onClick={() => {
                                    setValidationErrors(null);
                                    setNewConnectionApp(app);
                                    setNewConnectionName(null);
                                    setModalType('add-connection');
                                    setIsOpen(true);
                                }}
                            >
                                <div className="app-logo-wrapper">
                                    <AppLogo app={app} isActive={app.status === 'active'} />
                                    <div className="app-action-btns">
                                        <AddIcon
                                            key="add"
                                            className="app-action-btn"
                                            fill="var(--font-color)"
                                        />
                                    </div>
                                </div>
                                <p>{app.name}</p>
                            </div>
                        );
                    })}
                </div>
                <div className="header">Existing App Connections</div>
                <div className="app-select">
                    {apps?.rows?.map((app, i) => {
                        return app.connections.map((connection, j) => {
                            return (
                                <div
                                    className={`app-select-item ${
                                        connection.status !== 'failed' ? 'clickable' : ''
                                    }`}
                                    key={`${i}_${j}`}
                                    onClick={() => {
                                        // delete, when it is the only option (when status is 'active')
                                        if (connection.status !== 'failed') {
                                            deleteExistingConnection(connection);
                                        }
                                    }}
                                >
                                    <div className="app-logo-wrapper">
                                        <AppLogo
                                            app={app}
                                            isActive={connection.status === 'active'}
                                        />
                                        <div className="app-action-btns">
                                            {connection.status === 'failed' && (
                                                <RefreshIcon
                                                    className="app-action-btn"
                                                    fill="var(--warning-color)"
                                                    onClick={() => {
                                                        handleOAuth2(
                                                            app,
                                                            connection.name,
                                                            connection.id
                                                        ).then((response) => {
                                                            if (response?.error) {
                                                                setModalType('unavailable');
                                                                setIsOpen(true);
                                                            }
                                                        });
                                                    }}
                                                />
                                            )}
                                            <RemoveIcon
                                                className="app-action-btn"
                                                fill="var(--error-color)"
                                                onClick={(e) => {
                                                    deleteExistingConnection(connection);
                                                    e.stopPropagation();
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <p>{connection.name}</p>
                                </div>
                            );
                        });
                    })}
                </div>
            </div>
            <Modal options={getModalOptions(modalType)} isOpen={modalIsOpen} setOpen={setIsOpen} />
        </>
    );
};

export default Connections;
